import { QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { SignOutUnsavedDialog } from './app/sign-out-unsaved-dialog';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './lib/auth/auth-context';
import { initSentry } from './lib/observability/sentry';
import { reportWebVitals } from './lib/observability/web-vitals';
import { clearStaleChunkReloadFlag } from './lib/stale-chunk';
import { queryClient } from './lib/query/query-client';
import { router } from './routes/router';
import { TorBlockGate } from './routes/tor-blocked-page';
import './i18n';
import './index.css';

initSentry();
clearStaleChunkReloadFlag();

const root = document.getElementById('root');
if (!root) throw new Error('missing #root element');

/** Last-resort fallback if the whole app throws — plain HTML (no providers/i18n
 *  available at this level), with a reload path. Sentry captures the error. */
function GlobalErrorFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontWeight: 600, color: '#2A3470' }}>Something went wrong.</p>
        <button type="button" onClick={() => window.location.reload()} style={{ marginTop: 12, color: '#FF9900' }}>
          Reload
        </button>
      </div>
    </div>
  );
}

createRoot(root).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<GlobalErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* Once any response says TOR_BLOCKED the block page replaces the whole router (#118 R35). */}
          <TorBlockGate>
            <RouterProvider router={router} />
          </TorBlockGate>
          <SignOutUnsavedDialog />
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

reportWebVitals();
